import { useEffect, useState } from "react";
import { AlertTriangle, IndianRupee, Package, ShoppingBag, TrendingUp } from "lucide-react";
import api, { inr } from "@/lib/api";

const Overview = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-sand" />)}</div>;

  const cards = [
    { label: "Total sales", value: inr(stats.total_sales), icon: IndianRupee },
    { label: "Orders", value: stats.orders_count, icon: ShoppingBag, sub: `${stats.pending_orders} pending` },
    { label: "Avg. order value", value: inr(stats.aov), icon: TrendingUp },
    { label: "Products live", value: stats.products_count, icon: Package, sub: `${stats.low_stock} low · ${stats.out_of_stock} out` },
  ];
  const maxRevenue = Math.max(...stats.category_sales.map((c) => c.revenue), 1);

  return (
    <div data-testid="admin-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-linen bg-white p-6">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay/60">{c.label}</p>
              <c.icon className="h-4 w-4 text-terra" />
            </div>
            <p className="mt-3 font-serif text-3xl text-espresso">{c.value}</p>
            {c.sub && <p className="mt-1 text-xs text-clay/50">{c.sub}</p>}
          </div>
        ))}
      </div>

      {(stats.low_stock > 0 || stats.out_of_stock > 0) && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-ochre/40 bg-ochre/10 px-5 py-4">
          <AlertTriangle className="h-4 w-4 shrink-0 text-ochre" />
          <p className="text-sm text-clay">
            {stats.out_of_stock > 0 && <span className="font-semibold">{stats.out_of_stock} sold out</span>}
            {stats.out_of_stock > 0 && stats.low_stock > 0 && " · "}
            {stats.low_stock > 0 && <span className="font-semibold">{stats.low_stock} running low (under 6 units)</span>}
            {" — "}restock from the Products tab.
          </p>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-linen bg-white p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay/60">Sales by category</p>
        {stats.category_sales.length === 0 ? (
          <p className="mt-4 text-sm text-clay/50">No sales yet — they'll appear here after your first order.</p>
        ) : (
          <div className="mt-5 space-y-3.5">
            {stats.category_sales.map((c) => (
              <div key={c.category}>
                <div className="mb-1.5 flex justify-between text-xs">
                  <span className="font-medium text-espresso">{c.category}</span>
                  <span className="font-mono text-clay/70">{inr(c.revenue)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-sand">
                  <div className="h-full rounded-full bg-terra transition-all duration-700" style={{ width: `${(c.revenue / maxRevenue) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
